import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { LineupEditor } from "@/components/LineupEditor";
import { toast } from "sonner";
import {
  Radio, Plus, Trash2, ChevronDown, ChevronUp, Settings,
  Users, Activity, ClipboardList, Save, Target, Check, X, MonitorPlay,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const EVENT_TYPES = [
  { value: "goal", label: "Goal" },
  { value: "own_goal", label: "Own Goal" },
  { value: "penalty_goal", label: "Penalty (Scored)" },
  { value: "penalty_missed", label: "Penalty Missed" },
  { value: "yellow_card", label: "Yellow Card" },
  { value: "red_card", label: "Red Card" },
  { value: "substitution", label: "Substitution" },
  { value: "var", label: "VAR" },
  { value: "note", label: "Note" },
];

const STAT_FIELDS = [
  ["possession", "Possession %"],
  ["shots", "Shots"],
  ["shots_on_target", "Shots on Target"],
  ["corners", "Corners"],
  ["fouls", "Fouls"],
  ["yellow_cards", "Yellow Cards"],
  ["red_cards", "Red Cards"],
  ["offsides", "Offsides"],
  ["saves", "Saves"],
];

const Section = ({ icon: Icon, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-800">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
        data-testid={`console-section-${title.toLowerCase().replace(/\s/g, "-")}`}
      >
        <div className="flex items-center gap-2 text-sm font-heading uppercase tracking-widest text-gray-300">
          <Icon className="w-4 h-4 text-[#FFC72C]" />
          {title}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-3">{children}</div>}
    </div>
  );
};

/**
 * MatchAdminConsole — in-page control panel for the Match Centre.
 * Only rendered when the current user can edit this match.
 */
const MatchAdminConsole = ({ data, onChange }) => {
  const { match, home_team, away_team, statistics, competition, shootout } = data;

  const [collapsed, setCollapsed] = useState(false);
  const [playersHome, setPlayersHome] = useState([]);
  const [playersAway, setPlayersAway] = useState([]);

  // Load players for player-name dropdowns
  useEffect(() => {
    (async () => {
      try {
        const [h, a] = await Promise.all([
          axios.get(`${API}/players?team_id=${match.home_team_id}`),
          axios.get(`${API}/players?team_id=${match.away_team_id}`),
        ]);
        setPlayersHome(h.data || []);
        setPlayersAway(a.data || []);
      } catch (e) {
        console.error("Player load failed", e);
      }
    })();
  }, [match.home_team_id, match.away_team_id]);

  // ---------- LIVE STATUS / CLOCK ----------
  const [minute, setMinute] = useState(match.minute ?? 0);
  useEffect(() => { setMinute(match.minute ?? 0); }, [match.minute]);

  const clock = data.clock || {};
  const clockState = clock.clock_state || "pre_match";

  const setLive = async (payload) => {
    try {
      await axios.put(`${API}/matches/${match.match_id}/live-status`, payload, { withCredentials: true });
      toast.success("Match status updated");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const sendClockAction = async (action, extra = {}) => {
    try {
      await axios.post(
        `${API}/matches/${match.match_id}/clock`,
        { action, ...extra },
        { withCredentials: true },
      );
      toast.success(`Clock → ${action.replace(/_/g, " ")}`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Clock action '${action}' failed`);
    }
  };

  // ---------- ADD EVENT ----------
  const [eventForm, setEventForm] = useState({
    team_id: match.home_team_id,
    type: "goal",
    minute: "",
    added_time: "",
    player_id: "",
    secondary_player_id: "",
    detail: "",
  });

  const eventTeamPlayers = eventForm.team_id === match.home_team_id ? playersHome : playersAway;

  const submitEvent = async (e) => {
    e.preventDefault();
    // If match is live and minute is blank, let the server auto-fill from the clock.
    const liveLike = ["first_half", "second_half", "extra_time_first", "extra_time_second"].includes(clockState);
    const minuteBlank = eventForm.minute === "" || isNaN(parseInt(eventForm.minute, 10));
    if (minuteBlank && !liveLike) {
      toast.error("Minute is required (match is not live)");
      return;
    }
    const payload = {
      team_id: eventForm.team_id,
      type: eventForm.type,
      ...(minuteBlank ? {} : { minute: parseInt(eventForm.minute, 10) }),
      added_time: eventForm.added_time ? parseInt(eventForm.added_time, 10) : null,
      player_id: eventForm.player_id || null,
      secondary_player_id: eventForm.secondary_player_id || null,
      detail: eventForm.detail || null,
      // Idempotency key — guards double-click / network retries
      client_event_id: `evt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    };
    try {
      await axios.post(`${API}/matches/${match.match_id}/events`, payload, { withCredentials: true });
      toast.success("Event added");
      setEventForm((f) => ({ ...f, minute: "", added_time: "", player_id: "", secondary_player_id: "", detail: "" }));
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add event");
    }
  };

  const deleteEvent = async (event_id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/matches/${match.match_id}/events/${event_id}`, { withCredentials: true });
      toast.success("Event deleted");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete event");
    }
  };

  // ---------- STATISTICS ----------
  const [statsForm, setStatsForm] = useState({});
  useEffect(() => {
    if (!statistics) { setStatsForm({}); return; }
    const draft = {};
    STAT_FIELDS.forEach(([k]) => {
      draft[`${k}_home`] = statistics[`${k}_home`] ?? "";
      draft[`${k}_away`] = statistics[`${k}_away`] ?? "";
    });
    setStatsForm(draft);
  }, [statistics]);

  const saveStats = async () => {
    const payload = {};
    STAT_FIELDS.forEach(([k]) => {
      const hv = statsForm[`${k}_home`];
      const av = statsForm[`${k}_away`];
      payload[`${k}_home`] = hv === "" || hv == null ? null : parseInt(hv, 10);
      payload[`${k}_away`] = av === "" || av == null ? null : parseInt(av, 10);
    });
    try {
      await axios.put(`${API}/matches/${match.match_id}/statistics`, payload, { withCredentials: true });
      toast.success("Statistics saved");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save statistics");
    }
  };

  // ---------- LINEUPS ----------
  const [homeLineup, setHomeLineup] = useState({ formation: "", coach_name: "", starters: [], substitutes: [] });
  const [awayLineup, setAwayLineup] = useState({ formation: "", coach_name: "", starters: [], substitutes: [] });

  const [prefilled, setPrefilled] = useState({ home: null, away: null });
  const prefillTried = useRef({});

  useEffect(() => {
    const toState = (l) => ({
      formation: l.formation || "",
      coach_name: l.coach_name || "",
      starters: l.starters || [],
      substitutes: l.substitutes || [],
    });
    [["home", data.lineup_home, match.home_team_id, setHomeLineup], ["away", data.lineup_away, match.away_team_id, setAwayLineup]]
      .forEach(([side, saved, teamId, setter]) => {
        if (saved) { setter(toState(saved)); return; }
        const key = `${match.match_id}:${teamId}`;
        if (prefillTried.current[key]) return;
        prefillTried.current[key] = true;
        axios.get(`${API}/matches/${match.match_id}/previous-lineup/${teamId}`).then((r) => {
          if (!r.data) return;
          setter(toState(r.data));
          setPrefilled((p) => ({ ...p, [side]: r.data.from_match }));
        }).catch(() => {});
      });
  }, [data.lineup_home, data.lineup_away, match.match_id, match.home_team_id, match.away_team_id]);

  // ---------- PENALTY SHOOTOUT (knockouts only: cup / league) ----------
  const shootoutEnabled = ["cup", "league"].includes((competition?.type || "").toLowerCase());
  const shootoutKicks = shootout?.kicks || [];
  const shootoutHome = shootout?.home_score;
  const shootoutAway = shootout?.away_score;

  const [kickForm, setKickForm] = useState({
    team_id: match.home_team_id,
    player_id: "",
    scored: true,
  });

  const kickTeamPlayers = kickForm.team_id === match.home_team_id ? playersHome : playersAway;

  const submitKick = async (e) => {
    e.preventDefault();
    const player = kickTeamPlayers.find((p) => p.player_id === kickForm.player_id);
    if (!player) {
      toast.error("Select a player");
      return;
    }
    try {
      await axios.post(
        `${API}/matches/${match.match_id}/shootout`,
        {
          team_id: kickForm.team_id,
          player_id: player.player_id,
          player_name: player.name,
          scored: kickForm.scored,
        },
        { withCredentials: true },
      );
      toast.success(kickForm.scored ? "Kick scored" : "Kick missed");
      setKickForm((f) => ({ ...f, player_id: "", scored: true }));
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add kick");
    }
  };

  const deleteKick = async (kick_id) => {
    if (!window.confirm("Remove this kick?")) return;
    try {
      await axios.delete(`${API}/matches/${match.match_id}/shootout/${kick_id}`, { withCredentials: true });
      toast.success("Kick removed");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove kick");
    }
  };

  const events = useMemo(() => data.events || [], [data.events]);

  return (
    <div className="bg-[#09090b] text-white border border-[#FFC72C]/30 shadow-lg" data-testid="admin-console">
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#FFC72C]/10 to-transparent"
        data-testid="btn-toggle-console"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-[#FFC72C]" />
          <span className="font-heading uppercase text-sm tracking-widest">Match Console</span>
          <Badge className="bg-[#FFC72C] text-black text-[10px] hover:bg-[#FFC72C]">Admin</Badge>
        </div>
        {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
      </button>

      {!collapsed && (
        <>
          <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
            <a href={`/match/${match.match_id}/stadium`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFC72C] text-black text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors"
              data-testid="open-stadium-view-btn">
              <MonitorPlay className="w-4 h-4" /> Open Stadium View
            </a>
            <span className="text-[11px] text-gray-400">Opens the full-screen display in a new tab · refreshes every 30s</span>
          </div>
          {/* LIVE STATUS / CLOCK */}
          <Section icon={Radio} title="Match Clock" defaultOpen>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-400 mr-1">State:</span>
              <Badge className={`uppercase text-[10px] ${clockState === "first_half" || clockState === "second_half" || clockState === "extra_time_first" || clockState === "extra_time_second" ? "bg-red-500 hover:bg-red-500" : clockState === "paused" || clockState === "half_time" || clockState === "extra_time_half" ? "bg-amber-500 hover:bg-amber-500 text-black" : clockState === "full_time" ? "bg-[#009E60] hover:bg-[#009E60]" : "bg-white/10 hover:bg-white/10"} text-white`} data-testid="badge-clock-state">
                {clockState.replace(/_/g, " ")}
              </Badge>
              {clock.display_minute != null && (
                <Badge className="bg-white/10 hover:bg-white/10 font-mono" data-testid="badge-clock-minute">
                  {clock.display_added ? `${clock.display_minute}+${clock.display_added}'` : `${clock.display_minute}'`}
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-gray-500 -mt-1 mb-1">
              The clock advances automatically on all viewers' screens. Use these controls for official transitions only.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              {clockState === "pre_match" && (
                <Button size="sm" onClick={() => sendClockAction("start")} className="bg-red-500 hover:bg-red-600 h-8" data-testid="btn-clock-start">
                  <Radio className="w-3.5 h-3.5 mr-1" /> Start Match (Kickoff)
                </Button>
              )}
              {clockState === "first_half" && (
                <>
                  <Button size="sm" onClick={() => sendClockAction("halftime")} className="bg-amber-500 hover:bg-amber-600 text-black h-8" data-testid="btn-clock-halftime">
                    Half-Time
                  </Button>
                  <Button size="sm" onClick={() => sendClockAction("pause")} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white h-8" data-testid="btn-clock-pause">
                    Pause
                  </Button>
                </>
              )}
              {clockState === "half_time" && (
                <Button size="sm" onClick={() => sendClockAction("second_half")} className="bg-red-500 hover:bg-red-600 h-8" data-testid="btn-clock-second-half">
                  Start 2nd Half
                </Button>
              )}
              {clockState === "second_half" && (
                <>
                  <Button size="sm" onClick={() => sendClockAction("end")} className="bg-[#009E60] hover:bg-[#00834f] h-8" data-testid="btn-clock-end">
                    Full-Time
                  </Button>
                  <Button size="sm" onClick={() => sendClockAction("start_et_first")} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white h-8" data-testid="btn-clock-et">
                    Start Extra Time
                  </Button>
                  <Button size="sm" onClick={() => sendClockAction("pause")} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white h-8" data-testid="btn-clock-pause">
                    Pause
                  </Button>
                </>
              )}
              {clockState === "extra_time_first" && (
                <>
                  <Button size="sm" onClick={() => sendClockAction("et_halftime")} className="bg-amber-500 hover:bg-amber-600 text-black h-8" data-testid="btn-clock-et-ht">ET Half-Time</Button>
                  <Button size="sm" onClick={() => sendClockAction("pause")} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white h-8">Pause</Button>
                </>
              )}
              {clockState === "extra_time_half" && (
                <Button size="sm" onClick={() => sendClockAction("start_et_second")} className="bg-red-500 hover:bg-red-600 h-8">Start ET 2nd</Button>
              )}
              {clockState === "extra_time_second" && (
                <>
                  <Button size="sm" onClick={() => sendClockAction("end")} className="bg-[#009E60] hover:bg-[#00834f] h-8">Full-Time</Button>
                  <Button size="sm" onClick={() => sendClockAction("pause")} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white h-8">Pause</Button>
                </>
              )}
              {clockState === "paused" && (
                <Button size="sm" onClick={() => sendClockAction("resume")} className="bg-red-500 hover:bg-red-600 h-8" data-testid="btn-clock-resume">
                  Resume
                </Button>
              )}
              {["first_half", "second_half", "half_time", "extra_time_first", "extra_time_half", "extra_time_second", "paused"].includes(clockState) && (
                <Button size="sm" onClick={() => { if (window.confirm("Mark match as ABANDONED?")) sendClockAction("abandon"); }} variant="secondary" className="bg-red-500/20 hover:bg-red-500/30 text-red-300 h-8" data-testid="btn-clock-abandon">
                  Abandon
                </Button>
              )}
              {["full_time", "abandoned"].includes(clockState) && (
                <Button size="sm" onClick={() => { if (window.confirm("Reset the clock to pre-match? This DOES NOT delete events or scores.")) sendClockAction("reset"); }} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white h-8" data-testid="btn-clock-reset">
                  Reset Clock
                </Button>
              )}
            </div>

            {/* Legacy minute override — collapsed by default */}
            <details className="mt-2">
              <summary className="text-[11px] text-gray-500 cursor-pointer hover:text-gray-400">Legacy manual override (not recommended)</summary>
              <div className="flex flex-wrap gap-2 items-center mt-2">
                <Input
                  type="number"
                  min="0" max="120"
                  value={minute}
                  onChange={(e) => setMinute(parseInt(e.target.value || "0", 10))}
                  className="bg-white/10 border-white/10 text-white w-20 h-8 text-sm"
                  placeholder="Min"
                  data-testid="input-minute"
                />
                <Button size="sm" onClick={() => setLive({ is_live: true, minute, status: "live" })} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white h-8" data-testid="btn-update-minute">
                  Update legacy minute
                </Button>
                <Button size="sm" onClick={() => setLive({ is_live: false, status: "scheduled" })} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white h-8" data-testid="btn-reset-scheduled">
                  Reset
                </Button>
              </div>
            </details>
          </Section>

          {/* ADD EVENT */}
          <Section icon={Activity} title="Add Event" defaultOpen>
            <form onSubmit={submitEvent} className="space-y-2" data-testid="form-add-event">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <select
                  value={eventForm.team_id}
                  onChange={(e) => setEventForm((f) => ({ ...f, team_id: e.target.value, player_id: "", secondary_player_id: "" }))}
                  className="bg-white/10 border border-white/10 text-white text-xs h-8 px-2"
                  data-testid="select-event-team"
                >
                  <option value={match.home_team_id}>{home_team?.name || "Home"}</option>
                  <option value={match.away_team_id}>{away_team?.name || "Away"}</option>
                </select>
                <select
                  value={eventForm.type}
                  onChange={(e) => setEventForm((f) => ({ ...f, type: e.target.value }))}
                  className="bg-white/10 border border-white/10 text-white text-xs h-8 px-2"
                  data-testid="select-event-type"
                >
                  {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <Input
                  type="number" min="0" max="130"
                  placeholder={["first_half","second_half","extra_time_first","extra_time_second"].includes(clockState) ? "Min (auto)" : "Min *"}
                  value={eventForm.minute}
                  onChange={(e) => setEventForm((f) => ({ ...f, minute: e.target.value }))}
                  className="bg-white/10 border-white/10 text-white text-xs h-8"
                  data-testid="input-event-minute"
                />
                <Input
                  type="number" min="0" max="20"
                  placeholder="Added"
                  value={eventForm.added_time}
                  onChange={(e) => setEventForm((f) => ({ ...f, added_time: e.target.value }))}
                  className="bg-white/10 border-white/10 text-white text-xs h-8"
                  data-testid="input-event-added"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <select
                  value={eventForm.player_id}
                  onChange={(e) => setEventForm((f) => ({ ...f, player_id: e.target.value }))}
                  className="bg-white/10 border border-white/10 text-white text-xs h-8 px-2"
                  data-testid="select-event-player"
                >
                  <option value="">Player…</option>
                  {eventTeamPlayers.map((p) => (
                    <option key={p.player_id} value={p.player_id}>
                      {p.number ? `#${p.number} · ` : ""}{p.name}
                    </option>
                  ))}
                </select>
                <select
                  value={eventForm.secondary_player_id}
                  onChange={(e) => setEventForm((f) => ({ ...f, secondary_player_id: e.target.value }))}
                  className="bg-white/10 border border-white/10 text-white text-xs h-8 px-2"
                  data-testid="select-event-secondary"
                >
                  <option value="">
                    {eventForm.type === "substitution" ? "Substituted for…" : eventForm.type === "goal" ? "Assist by…" : "Secondary player…"}
                  </option>
                  {eventTeamPlayers.map((p) => (
                    <option key={p.player_id} value={p.player_id}>
                      {p.number ? `#${p.number} · ` : ""}{p.name}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Detail (Foul, etc)"
                  value={eventForm.detail}
                  onChange={(e) => setEventForm((f) => ({ ...f, detail: e.target.value }))}
                  className="bg-white/10 border-white/10 text-white text-xs h-8"
                  data-testid="input-event-detail"
                />
              </div>
              <Button type="submit" size="sm" className="bg-[#FFC72C] text-black hover:bg-[#e6b325] h-8" data-testid="btn-submit-event">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Event
              </Button>
            </form>

            {events.length > 0 && (
              <div className="mt-3 border-t border-white/10 pt-3">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">Existing ({events.length})</p>
                <ul className="space-y-1 max-h-56 overflow-y-auto">
                  {events.map((e) => (
                    <li key={e.event_id} className="flex items-center gap-2 bg-white/5 px-2 py-1 text-xs" data-testid={`console-event-${e.event_id}`}>
                      <span className="font-mono w-12 text-gray-400 text-right">{e.minute}{e.added_time ? `+${e.added_time}` : ""}'</span>
                      <span className={`px-1.5 py-0.5 text-[10px] uppercase font-bold ${e.team_id === match.home_team_id ? "bg-[#009E60]" : "bg-[#FFC72C] text-black"}`}>
                        {e.team_id === match.home_team_id ? "H" : "A"}
                      </span>
                      <span className="flex-1 truncate">
                        <span className="text-gray-300">{e.type}</span>
                        <span className="text-white ml-2">{e.player_name || "—"}</span>
                        {e.secondary_player_name && <span className="text-gray-400 ml-1">→ {e.secondary_player_name}</span>}
                        {e.detail && <span className="text-gray-500 ml-1">· {e.detail}</span>}
                      </span>
                      <button onClick={() => deleteEvent(e.event_id)} className="text-red-400 hover:text-red-300" data-testid={`btn-delete-event-${e.event_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {/* LINEUPS */}
          <Section icon={Users} title="Lineups">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LineupEditor side="home" state={homeLineup} setState={setHomeLineup} teamName={home_team?.name || "Home"} players={playersHome}
                matchId={match.match_id} teamId={match.home_team_id} onChange={onChange} prefilledFrom={prefilled.home} onSaved={() => setPrefilled((p) => ({ ...p, home: null }))} />
              <LineupEditor side="away" state={awayLineup} setState={setAwayLineup} teamName={away_team?.name || "Away"} players={playersAway}
                matchId={match.match_id} teamId={match.away_team_id} onChange={onChange} prefilledFrom={prefilled.away} onSaved={() => setPrefilled((p) => ({ ...p, away: null }))} />
            </div>
          </Section>

          {/* STATISTICS */}
          <Section icon={ClipboardList} title="Statistics">
            <div className="space-y-1">
              <div className="grid grid-cols-[1fr_60px_60px] gap-2 items-center text-[10px] uppercase text-gray-400 mb-1">
                <span></span>
                <span className="text-center">Home</span>
                <span className="text-center">Away</span>
              </div>
              {STAT_FIELDS.map(([key, label]) => (
                <div key={key} className="grid grid-cols-[1fr_60px_60px] gap-2 items-center">
                  <span className="text-xs text-gray-300">{label}</span>
                  <Input
                    type="number"
                    value={statsForm[`${key}_home`] ?? ""}
                    onChange={(e) => setStatsForm((s) => ({ ...s, [`${key}_home`]: e.target.value }))}
                    className="bg-white/10 border-white/10 text-white text-xs h-8 text-center"
                    data-testid={`input-stat-${key}-home`}
                  />
                  <Input
                    type="number"
                    value={statsForm[`${key}_away`] ?? ""}
                    onChange={(e) => setStatsForm((s) => ({ ...s, [`${key}_away`]: e.target.value }))}
                    className="bg-white/10 border-white/10 text-white text-xs h-8 text-center"
                    data-testid={`input-stat-${key}-away`}
                  />
                </div>
              ))}
              <Button size="sm" onClick={saveStats} className="bg-[#FFC72C] text-black hover:bg-[#e6b325] h-8 mt-2" data-testid="btn-save-stats">
                <Save className="w-3.5 h-3.5 mr-1" /> Save Statistics
              </Button>
            </div>
          </Section>

          {/* PENALTY SHOOTOUT (cup / league knockouts) */}
          {shootoutEnabled && (
            <Section icon={Target} title="Penalty Shootout">
              <p className="text-[11px] text-gray-500 -mt-1 mb-2">
                For knockout ties still level after 120'. Score is shown in match header as "(X-Y pens)".
              </p>

              {/* Aggregate */}
              <div className="grid grid-cols-3 items-center gap-2 py-2 border-y border-white/10 mb-3" data-testid="shootout-aggregate">
                <div className="text-right">
                  <p className="text-[10px] uppercase text-gray-500">{home_team?.name || "Home"}</p>
                  <p className="text-2xl font-heading font-black text-[#FFC72C]" data-testid="shootout-home-score">
                    {shootoutHome ?? 0}
                  </p>
                </div>
                <div className="text-center text-gray-500 text-xs uppercase tracking-widest">
                  Pens
                </div>
                <div className="text-left">
                  <p className="text-[10px] uppercase text-gray-500">{away_team?.name || "Away"}</p>
                  <p className="text-2xl font-heading font-black text-[#FFC72C]" data-testid="shootout-away-score">
                    {shootoutAway ?? 0}
                  </p>
                </div>
              </div>

              {/* Add kick */}
              <form onSubmit={submitKick} className="space-y-2" data-testid="shootout-add-form">
                <div className="flex items-center gap-2">
                  <select
                    value={kickForm.team_id}
                    onChange={(e) => setKickForm((f) => ({ ...f, team_id: e.target.value, player_id: "" }))}
                    className="bg-white/10 border border-white/10 text-white text-xs h-8 px-2"
                    data-testid="shootout-select-team"
                  >
                    <option value={match.home_team_id}>{home_team?.name || "Home"}</option>
                    <option value={match.away_team_id}>{away_team?.name || "Away"}</option>
                  </select>
                  <select
                    value={kickForm.player_id}
                    onChange={(e) => setKickForm((f) => ({ ...f, player_id: e.target.value }))}
                    className="flex-1 bg-white/10 border border-white/10 text-white text-xs h-8 px-2"
                    data-testid="shootout-select-player"
                    required
                  >
                    <option value="">Select taker…</option>
                    {kickTeamPlayers.map((p) => (
                      <option key={p.player_id} value={p.player_id}>
                        {p.number ? `#${p.number} · ` : ""}{p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setKickForm((f) => ({ ...f, scored: true }))}
                    className={`flex-1 flex items-center justify-center gap-1 h-8 text-xs font-bold uppercase tracking-widest transition ${kickForm.scored ? "bg-green-500 text-black" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}
                    data-testid="shootout-btn-scored"
                  >
                    <Check className="w-3.5 h-3.5" /> Scored
                  </button>
                  <button
                    type="button"
                    onClick={() => setKickForm((f) => ({ ...f, scored: false }))}
                    className={`flex-1 flex items-center justify-center gap-1 h-8 text-xs font-bold uppercase tracking-widest transition ${!kickForm.scored ? "bg-red-500 text-white" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}
                    data-testid="shootout-btn-missed"
                  >
                    <X className="w-3.5 h-3.5" /> Missed
                  </button>
                  <Button
                    type="submit"
                    size="sm"
                    className="bg-[#FFC72C] text-black hover:bg-[#e6b325] h-8 px-3"
                    data-testid="shootout-add-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
              </form>

              {/* Kicks list */}
              {shootoutKicks.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase text-gray-500 mb-1">Kicks · {shootoutKicks.length}</p>
                  <ul className="space-y-0.5 max-h-56 overflow-y-auto pr-1">
                    {shootoutKicks.map((k) => {
                      const isHome = k.team_id === match.home_team_id;
                      return (
                        <li
                          key={k.kick_id}
                          className="flex items-center gap-2 bg-white/5 px-2 py-1.5 text-xs"
                          data-testid={`shootout-kick-${k.kick_id}`}
                        >
                          <span className="font-mono text-gray-400 w-6 text-right">{k.order}.</span>
                          <span className={`w-1 h-4 ${isHome ? "bg-[#FFC72C]" : "bg-blue-400"}`} />
                          <span className="flex-1 text-white truncate">{k.player_name}</span>
                          <span className="text-[10px] text-gray-400 uppercase">
                            {isHome ? (home_team?.short_name || "H") : (away_team?.short_name || "A")}
                          </span>
                          {k.scored ? (
                            <span className="text-green-400 flex items-center gap-0.5"><Check className="w-3.5 h-3.5" /></span>
                          ) : (
                            <span className="text-red-400 flex items-center gap-0.5"><X className="w-3.5 h-3.5" /></span>
                          )}
                          <button
                            onClick={() => deleteKick(k.kick_id)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`shootout-delete-${k.kick_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default MatchAdminConsole;
